// PidModal.jsx — centered modal scaffold for the Package Index admin Add/Edit surfaces.
// Sticky cream header (title + display-only status pill + close), scrollable body,
// sticky footer (destructive action left, Cancel/Save right). Full-screen at <=640px.
// Follows the Customer Specs Edit modal pattern (v1.04.57) and the owner-approved
// PID tables mockup. Plan: the-vault-pid-tables-ux-2026-07 (M3).
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { pidStatusColor, normalizePidStatus } from '../utils/pidStatus';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5;
  background: var(--color-overlay);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 22px;
  padding-left: calc(var(--nav-width, 0px) + 22px);

  @media (max-width: 640px) {
    padding: 0;
    padding-left: var(--nav-width, 0px);
  }
`;

const ModalBox = styled.div`
  background: var(--cream-50);
  border-radius: 16px;
  width: min(1100px, 100%);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 18px 60px var(--shadow-a25);
  overflow: hidden;

  @media (max-width: 640px) {
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }
`;

const MHead = styled.div`
  background: var(--cream-100);
  border-bottom: 1px solid var(--cream-500);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: none;
`;

const MTitle = styled.h2`
  margin: 0;
  font-family: var(--font-heading);
  font-size: 1.1rem;
  color: var(--green-800);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`;

const CloseBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: var(--color-text);
  line-height: 0;
  padding: 6px;
  border-radius: 8px;

  &:hover { background: var(--shadow-a05); }
  &:focus-visible { outline: 2px solid var(--green-a45); }
`;

const MBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 18px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 640px) { padding: 16px 14px 22px; }
`;

const MFoot = styled.div`
  background: var(--cream-100);
  border-top: 1px solid var(--cream-500);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: none;
  flex-wrap: wrap;
`;

export const FootActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

export const PrimaryButton = styled.button`
  font: inherit;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px 18px;
  background: var(--green-700);
  color: var(--white);
  transition: background 0.2s ease;

  &:hover { background: var(--green-800); }
  &:disabled { background: var(--grey-400); cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--green-a45); outline-offset: 2px; }
`;

export const QuietButton = styled.button`
  font: inherit;
  font-weight: 600;
  border: 1px solid var(--grey-300);
  border-radius: 8px;
  cursor: pointer;
  padding: 8px 16px;
  background: var(--white-a66);
  color: var(--color-text);

  &:hover { background: var(--white); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--green-a45); outline-offset: 2px; }
`;

/* ---- body building blocks shared by the per-kind modals ---- */

export const MSection = styled.div``;

export const MSectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--green-700);
`;

export const MSectionNote = styled.div`
  font-size: 0.78rem;
  color: var(--ink-a60);
  margin: -4px 0 8px;
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px 18px;
`;

const DetailBox = styled.div`
  background: var(--white-a66);
  border: 1px solid var(--cream-500);
  border-radius: 8px;
  padding: 8px 10px;
`;

const DetailLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ink-a45);
`;

const DetailValue = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

export const DetailItem = ({ label, value }) => (
  <DetailBox>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>{value === null || value === undefined || value === '' ? '—' : value}</DetailValue>
  </DetailBox>
);

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px 16px;
`;

export const FieldWrap = styled.div``;

export const FieldLabel = styled.label`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ink-a60);
  margin-bottom: 4px;
`;

export const FieldInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  padding: 8px 10px;
  color: var(--color-text);
  background: var(--white-a66);
  border: 1px solid var(--cream-500);
  border-radius: 8px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--green-800);
    box-shadow: 0 0 0 1px var(--green-800);
  }
`;

export const FieldError = styled.div`
  color: var(--color-danger);
  margin-top: 0.25rem;
  font-size: 0.8rem;
`;

/* image grid pieces (per-kind modals wire their own fetch/delete flows) */

export const ImgGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
`;

export const ImgWrap = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: var(--white);
  border: ${({ $marked }) => ($marked ? '2px solid var(--color-danger)' : '1px solid var(--grey-200)')};
`;

export const ImgActionBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: ${({ $danger }) => ($danger ? 'var(--color-danger)' : 'var(--white-a90)')};
  color: ${({ $danger }) => ($danger ? 'var(--white)' : 'var(--black)')};
  border: none;
  border-radius: 999px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PidModal = ({ title, status, onClose, footerLeft, footerRight, children }) => {
  const closeRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const pill = status ? pidStatusColor(status) : null;

  return (
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <ModalBox role="dialog" aria-modal="true" aria-label={title}>
        <MHead>
          <MTitle>{title}</MTitle>
          {pill && (
            <StatusPill $bg={pill.bg} $fg={pill.fg}>{normalizePidStatus(status)}</StatusPill>
          )}
          <CloseBtn ref={closeRef} type="button" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </CloseBtn>
        </MHead>
        <MBody>{children}</MBody>
        <MFoot>
          <FootActions>{footerLeft}</FootActions>
          <FootActions>{footerRight}</FootActions>
        </MFoot>
      </ModalBox>
    </Overlay>
  );
};

export default PidModal;
